import { z } from "zod";

export const CURRENT_SAVE_SCHEMA_VERSION = 1;

export type JsonPrimitive = boolean | number | string | null;
export type JsonValue =
  JsonPrimitive | JsonValue[] | { readonly [key: string]: JsonValue };

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

const jsonObjectSchema = z.record(z.string(), jsonValueSchema);

export const activeRunSchema = z
  .object({
    checkpointVersion: z.number().int().positive(),
    appVersion: z.string().min(1),
    contentVersion: z.string().min(1),
    simulationVersion: z.number().int().positive(),
    seed: z.number().int().nonnegative(),
    tick: z.number().int().nonnegative(),
    lastCompletedRound: z.number().int().nonnegative(),
    checkpointedAt: z.iso.datetime(),
    payload: jsonValueSchema,
  })
  .strict();

export const saveEnvelopeSchema = z
  .object({
    saveSchemaVersion: z.literal(CURRENT_SAVE_SCHEMA_VERSION),
    gameVersion: z.string().min(1),
    contentVersion: z.string().min(1),
    simulationVersion: z.number().int().positive(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    profile: jsonObjectSchema,
    progression: jsonObjectSchema,
    settings: jsonObjectSchema,
    statistics: jsonObjectSchema,
    activeRun: activeRunSchema.nullable(),
    integrity: z
      .object({
        revision: z.number().int().nonnegative(),
        lastWriteId: z.string().min(1),
      })
      .strict(),
  })
  .strict();

export type ActiveRunSave = z.infer<typeof activeRunSchema>;
export type SaveEnvelope = z.infer<typeof saveEnvelopeSchema>;

export function parseSaveEnvelope(value: unknown): SaveEnvelope {
  return saveEnvelopeSchema.parse(value);
}

export function readSaveSchemaVersion(value: unknown): number {
  if (
    typeof value !== "object" ||
    value === null ||
    !("saveSchemaVersion" in value)
  ) {
    throw new Error("Save is missing saveSchemaVersion.");
  }

  const version = (value as { saveSchemaVersion?: unknown }).saveSchemaVersion;
  if (
    !Number.isInteger(version) ||
    typeof version !== "number" ||
    version < 1
  ) {
    throw new Error("Save has an invalid saveSchemaVersion.");
  }
  return version;
}
